import { useState } from 'react';
import { motion } from 'motion/react';
import { Trophy, TrendingUp } from 'lucide-react';
import { ImageWithFallback } from '../components/ImageWithFallback';

function LeaderboardPage({ user }) {
  const [sortBy, setSortBy] = useState('votes');
  const [dishType, setDishType] = useState('all');
  const [cuisine, setCuisine] = useState('all');

  const trendingDishes = [
    { rank: 1, name: 'Salmon Sushi Platter', votes: 156, percentage: 100 },
    { rank: 2, name: 'Ribeye Steak', votes: 134, percentage: 86 },
    { rank: 3, name: 'Margherita Pizza', votes: 128, percentage: 82 },
    { rank: 4, name: 'Tonkotsu Ramen', votes: 112, percentage: 72 },
    { rank: 5, name: 'Chicken Tikka Masala', votes: 103, percentage: 66 },
  ];

  const topDishes = [
    {
      id: '1',
      rank: 1,
      name: 'Salmon Sushi Platter',
      restaurant: 'Sakura Sushi',
      cuisine: 'Japanese',
      type: 'Sushi',
      rating: 4.9,
      votes: 156,
      image: 'https://images.unsplash.com/photo-1763647756796-af9230245bf8?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxzYWxtb24lMjBzdXNoaSUyMHBsYXR0ZXJ8ZW58MXx8fHwxNzY0MDk2OTk1fDA&ixlib=rb-4.1.0&q=80&w=1080',
    },
    {
      id: '2',
      rank: 2,
      name: 'Ribeye Steak',
      restaurant: 'Steakhouse Prime',
      cuisine: 'American',
      type: 'Steak',
      rating: 4.8,
      votes: 134,
      image: 'https://images.unsplash.com/photo-1619719015339-133a130520f6?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxyaWJleWUlMjBzdGVhayUyMHBsYXRlfGVufDF8fHx8MTc2NDA5Njk5NXww&ixlib=rb-4.1.0&q=80&w=1080',
    },
    {
      id: '3',
      rank: 3,
      name: 'Margherita Pizza',
      restaurant: 'Bella Italia',
      cuisine: 'Italian',
      type: 'Pizza',
      rating: 4.8,
      votes: 128,
      image: 'https://images.unsplash.com/photo-1602658015584-790c9302078f?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxtYXJnaGVyaXRhJTIwcGl6emElMjB3b29kfGVufDF8fHx8MTc2NDA4MjMyM3ww&ixlib=rb-4.1.0&q=80&w=1080',
    },
    {
      id: '4',
      rank: 4,
      name: 'Tonkotsu Ramen',
      restaurant: 'Ramen House',
      cuisine: 'Japanese',
      type: 'Ramen',
      rating: 4.7,
      votes: 112,
      image: 'https://images.unsplash.com/photo-1635379511574-bc167ca085c8?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHx0b25rb3RzdSUyMHJhbWVuJTIwYm93bHxlbnwxfHx8fDE3NjQwOTY5OTZ8MA&ixlib=rb-4.1.0&q=80&w=1080',
    },
  ];

  return (
    <div className="container mx-auto px-8 max-w-7xl">
      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center mb-20 pt-8"
      >
        <div className="w-1 h-20 bg-border mx-auto mb-8"></div>
        <h1 className="serif text-7xl mb-6">Dish Leaderboard</h1>
        <p className="text-muted-foreground max-w-2xl mx-auto leading-relaxed">
          Discover the most popular dishes based on community votes
        </p>
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="grid grid-cols-3 gap-6 mb-16 pb-16 border-b border-border"
      >
        <div>
          <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-3">
            Sort by:
          </label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="w-full px-4 py-3 border border-border bg-card text-foreground text-sm"
          >
            <option value="votes">Most Votes</option>
            <option value="rating">Highest Rating</option>
            <option value="recent">Most Recent</option>
          </select>
        </div>

        <div>
          <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-3">
            Dish Type:
          </label>
          <select
            value={dishType}
            onChange={(e) => setDishType(e.target.value)}
            className="w-full px-4 py-3 border border-border bg-card text-foreground text-sm"
          >
            <option value="all">All Types</option>
            <option value="sushi">Sushi</option>
            <option value="steak">Steak</option>
            <option value="pizza">Pizza</option>
            <option value="ramen">Ramen</option>
          </select>
        </div>

        <div>
          <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-3">
            Cuisine:
          </label>
          <select
            value={cuisine}
            onChange={(e) => setCuisine(e.target.value)}
            className="w-full px-4 py-3 border border-border bg-card text-foreground text-sm"
          >
            <option value="all">All Cuisines</option>
            <option value="japanese">Japanese</option>
            <option value="american">American</option>
            <option value="italian">Italian</option>
            <option value="indian">Indian</option>
          </select>
        </div>
      </motion.div>

      {/* Trending This Month */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="mb-24"
      >
        <div className="flex items-center gap-3 mb-12">
          <TrendingUp className="w-5 h-5" />
          <h2 className="serif text-4xl">Trending This Month</h2>
        </div>

        <div className="space-y-6">
          {trendingDishes.map((dish, index) => (
            <motion.div
              key={dish.rank}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 * index }}
              className="flex items-center gap-6"
            >
              {/* Rank */}
              <div className="w-12 text-center">
                <span className="serif text-3xl text-muted-foreground">#{dish.rank}</span>
              </div>

              {/* Progress Bar Container */}
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm">{dish.name}</span>
                  <span className="text-xs text-muted-foreground">{dish.votes} votes</span>
                </div>
                <div className="h-2 bg-muted relative overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${dish.percentage}%` }}
                    transition={{ duration: 1, delay: 0.2 + index * 0.1, ease: 'easeOut' }}
                    className="h-full bg-foreground"
                  />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* Top Dishes Grid */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.5 }}
      >
        <div className="flex items-center gap-3 mb-12">
          <Trophy className="w-5 h-5" />
          <h2 className="serif text-4xl">Top Dishes</h2>
        </div>

        <div className="grid grid-cols-4 gap-8">
          {topDishes.map((dish, index) => (
            <motion.article
              key={dish.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 * index }}
              className="group"
            >
              {/* Image */}
              <div className="relative aspect-square mb-4 overflow-hidden">
                <ImageWithFallback
                  src={dish.image}
                  alt={dish.name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                
                {/* Rank Badge */}
                <div className="absolute top-3 left-3 w-10 h-10 bg-foreground text-background flex items-center justify-center">
                  <span className="serif text-lg">#{dish.rank}</span>
                </div>

                {/* Rating Badge */}
                <div className="absolute top-3 right-3 bg-white/95 backdrop-blur-sm px-3 py-1.5">
                  <span className="text-xs">★ {dish.rating}</span>
                </div>
              </div>

              {/* Content */}
              <div>
                <h3 className="serif text-xl mb-2 group-hover:text-muted-foreground transition-colors">
                  {dish.name}
                </h3>

                <p className="text-sm text-muted-foreground mb-3">{dish.restaurant}</p>

                <div className="flex items-center gap-2 mb-4">
                  <span className="text-xs px-2 py-1 border border-border">{dish.cuisine}</span>
                  <span className="text-xs px-2 py-1 border border-border">{dish.type}</span>
                </div>

                <div className="h-px bg-border mb-4"></div>

                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">{dish.votes} ratings</span>
                  <button className="text-xs uppercase tracking-widest hover:text-muted-foreground transition-colors">
                    View
                  </button>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </motion.section>
    </div>
  );
}

export default LeaderboardPage;
